import { useEffect, useRef, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import 'chart.js/auto';
import type { PlanningSplit } from '@/modules/pipelines/data/metrics';
import {
  BreakdownLegend,
  type BreakdownSegment,
} from '@/modules/pipelines/components/deep-dive/asset-metrics/breakdown-bar';
import styles from '@/modules/pipelines/components/deep-dive/asset-metrics/notice-planning-split.module.scss';

interface ResolvedColors {
  critical: string;
  planned: string;
  neutralSoft: string;
  surface: string;
}

const FALLBACK: ResolvedColors = {
  critical: '#d03b3b',
  planned: '#2a78d6',
  neutralSoft: '#d6d6d1',
  surface: '#ffffff',
};

export default function NoticePlanningSplit({
  planning,
  windowLabel,
}: {
  planning: PlanningSplit;
  windowLabel: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [colors, setColors] = useState<ResolvedColors>(FALLBACK);

  useEffect(() => {
    const resolve = () => {
      const element = wrapperRef.current;
      if (!element) return;

      const style = getComputedStyle(element);
      const read = (name: string, fallback: string) =>
        style.getPropertyValue(name).trim() || fallback;

      setColors({
        critical: read('--status-critical', FALLBACK.critical),
        planned: read('--status-planned', FALLBACK.planned),
        neutralSoft: read('--status-neutral-soft', FALLBACK.neutralSoft),
        surface: read('--bs-body-bg', FALLBACK.surface),
      });
    };

    resolve();

    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    });

    return () => observer.disconnect();
  }, []);

  const segments: BreakdownSegment[] = [
    { key: 'unplanned', label: 'Unplanned', count: planning.unplanned, color: colors.critical },
    { key: 'planned', label: 'Planned', count: planning.planned, color: colors.planned },
    { key: 'unknown', label: 'Unknown', count: planning.unknown, color: colors.neutralSoft },
  ];

  const total = planning.planned + planning.unplanned + planning.unknown;
  const visible = segments.filter(segment => segment.count > 0);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.header}>
        <span className={styles.title}>Notices by planning status</span>
        <span className={styles.subtitle}>{windowLabel}</span>
      </div>

      {total === 0 && <div className={styles.empty}>No notices in this window.</div>}

      {total > 0 && (
        <>
          <div className={styles.chart}>
            <Pie
              data={{
                labels: visible.map(segment => segment.label),
                datasets: [
                  {
                    data: visible.map(segment => segment.count),
                    backgroundColor: visible.map(segment => segment.color),
                    borderColor: colors.surface,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                maintainAspectRatio: true,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: context => {
                        const value = context.parsed;
                        const share = total > 0 ? Math.round((value / total) * 100) : 0;
                        return `${value.toLocaleString()} · ${share}%`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
          <BreakdownLegend segments={segments} />
        </>
      )}
    </div>
  );
}
